// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { create } from 'zustand';
import { persist, type PersistOptions } from 'zustand/middleware';

/** Why the screen is currently locked — drives copy, not behavior. */
export type LockReason = 'idle' | 'unauthorized' | 'account_disabled' | null;

export interface SessionGuardState {
  /**
   * Epoch ms of the most recent successful sign-in. Persisted: the 12-hour
   * absolute limit is "since sign-in," not "since last reload" — if this
   * reset on every page load, a refresh every ~11h59m would defeat the cap
   * entirely.
   */
  signInAt: number | null;
  /**
   * Wall-display exemption, set once from a `?kiosk=1` visit (see
   * `SessionGuard`) and never unset by any in-app action. Persisted so it
   * survives reload/restart on that physical device.
   */
  kiosk: boolean;

  /** Epoch ms of the last real user input. NOT persisted — re-armed on load. */
  lastActivityAt: number;
  /** True while the lock overlay should render. NOT persisted. */
  locked: boolean;
  lockReason: LockReason;
  /** True while the idle warning modal should render. NOT persisted. */
  warningVisible: boolean;

  recordActivity: () => void;
  showWarning: () => void;
  dismissWarning: () => void;
  /**
   * Locks the screen. This is the single choke point both idle-timeout and
   * a 401-from-any-call (see `services/api.ts`) route through, so they
   * produce the exact same overlay. If Admin Center's future API layer
   * ever needs to reach this itself, that must go through a narrow
   * `shared/session/` facade (e.g. `reportUnauthorized()`), never a direct
   * import of this store (ADR-0014) — but check first whether it even
   * needs to: Admin Center's `shared/services/*` wrappers (e.g.
   * `sopFactoryService.ts`, added by the Procedures/Library PRs) already
   * call `get()` straight from the real `services/api.ts` — an explicitly
   * allowed exception (see `shared/README.md`) — which means their 401s
   * already flow through the one call site above and lock the screen
   * automatically. No new facade is needed unless some future Admin
   * Center API layer stops routing through that `get()`.
   */
  lock: (_reason: LockReason) => void;
  unlock: () => void;
  setSignInAt: (_ts: number) => void;
  clearSignInAt: () => void;
  setKiosk: (_value: boolean) => void;
}

type SessionGuardPersist = Pick<SessionGuardState, 'signInAt' | 'kiosk'>;

const persistOptions: PersistOptions<SessionGuardState, SessionGuardPersist> = {
  name: 'helios_session_guard',
  partialize: state => ({
    signInAt: state.signInAt,
    kiosk: state.kiosk,
  }),
};

export const useSessionGuardStore = create<SessionGuardState>()(
  persist(
    set => ({
      signInAt: null,
      kiosk: false,

      lastActivityAt: Date.now(),
      locked: false,
      lockReason: null,
      warningVisible: false,

      recordActivity: () => set({ lastActivityAt: Date.now() }),
      showWarning: () => set({ warningVisible: true }),
      dismissWarning: () => set({ warningVisible: false }),
      // A deactivated account stays the reason: a later idle or 401 lock
      // would otherwise offer an Unlock that can't work.
      lock: reason =>
        set(state => ({
          locked: true,
          lockReason: state.lockReason === 'account_disabled' ? state.lockReason : reason,
          warningVisible: false,
        })),
      unlock: () =>
        set({ locked: false, lockReason: null, warningVisible: false, lastActivityAt: Date.now() }),
      setSignInAt: ts => set({ signInAt: ts }),
      clearSignInAt: () => set({ signInAt: null }),
      setKiosk: value => set({ kiosk: value }),
    }),
    persistOptions
  )
);
